import React, { useContext, useEffect, useState } from "react";
import { ref, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Button,
  Divider,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { AuthContext } from "components/Auth";
import { Layout as DashboardLayout } from 'layouts/portal/layout';
import { remove_user, getAccount } from 'components/Auth';

export const Users = () => {
  const navigate = useNavigate();
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [attachedUsers, setAttachedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeDialog, setRemoveDialog] = useState({ open: false, user: null });
  const [maxUsers, setMaxUsers] = useState(1);
  const { account, currentUser, selectedStore, database } = useContext(AuthContext);

  const fetchUsersData = async () => {
    try {
      setLoading(true);
      // console.log('Fetching users data...', { account, acctOwner: account.acctOwner });
      
      // Get the owner information
      if (account.acctOwner) {
        const ownerRef = ref(database, `users/${account.acctOwner}`);
        const ownerSnapshot = await get(ownerRef);
        const ownerData = ownerSnapshot.val();
        
        // console.log('Owner data:', ownerData);
        
        if (ownerData) {
          setOwnerInfo({
            id: account.acctOwner,
            email: ownerData.userEmail || '',
            phoneNumber: ownerData.phoneNumber || '',
            countryCode: ownerData.countryCode || '',
            userRole: ownerData.userRole || 1 // Default to Owner if not specified
          });
        }
      }

      // Get attached users (non-owners)
      if (account.acctUsers) {
        // console.log('Account users:', account.acctUsers);
        const userPromises = Object.keys(account.acctUsers)
          .filter(userId => userId !== account.acctOwner) // Exclude owner
          .map(async (userId) => {
            const userRef = ref(database, `users/${userId}`);
            const userSnapshot = await get(userRef);
            const userData = userSnapshot.val();
            
            if (userData) {
              return {
                id: userId,
                firstName: userData.userFirstName || '',
                lastName: userData.userLastName || '',
                email: userData.userEmail || '',
                phoneNumber: userData.phoneNumber || '',
                countryCode: userData.countryCode || '',
                userRole: userData.userRole || 4,
                invitationStatus: userData.invitationStatus ?? 2 // Optional field, set to 2 default
              };
            }
            return null;
          });

        const users = await Promise.all(userPromises);
        setAttachedUsers(users.filter(user => user !== null));
      }

      // Fetch enriched account data using cloud function to get maxUsers
      // Derive accountId
      let acctId = account?.id || account?.accountId || account?.acctId;
      if (!acctId && currentUser?.uid) {
        try {
          const currentUserRef = ref(database, `users/${currentUser.uid}`);
          const currentUserSnapshot = await get(currentUserRef);
          const currentUserData = currentUserSnapshot.val();
          acctId = currentUserData?.primaryAccount || currentUserData?.userAccount;
        } catch (e) {
          console.log("Failed to derive account ID from user data");
        }
      }

      if (acctId && currentUser) {
        get(ref(database, `subscriptionPlans/${account?.subscription?.stripePlanID}`)).then((data) => {
          setMaxUsers(data?.val()?.maxUsers || 1)
        })
      }
    } catch (error) {
      console.error('Error fetching users data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (account && database) {
      fetchUsersData();
    }
  }, [account, database, selectedStore]);

  const formatPhoneNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber) return '';
    if (countryCode) {
      return `+${countryCode} ${phoneNumber}`;
    }
    return phoneNumber;
  };

  const getUserRoleLabel = (userRole) => {
    switch (userRole) {
      case 1: return 'OWNER';
      case 2: return 'ADMIN';
      case 3: return 'BUYER';
      case 4: return 'VIEWER';
      default: return 'USER';
    }
  };

  const getUserBadgeLabel = (user) => {
    return user.invitationStatus === 1 ? 'INVITED' : getUserRoleLabel(user.userRole);
  };

  const getUserCapacityMessage = () => {
    if (maxUsers == null) return null;
    if (maxUsers === 1) return 'Your subscription does not include additional users in total.';
    const additional = maxUsers - 1; 
    return `You can invite up to ${additional} user${additional === 1 ? '' : 's'} to your account.`;
  };

  const handleAddUser = () => {
    navigate('/users/add');
  };

  const handleRemoveUser = (user) => {
    setRemoveDialog({ open: true, user });
  };
  
  const handleRemoveUserConfirm = async () => {
    if (!removeDialog.user) return;

    try {
      setRemoveLoading(true);
          
      // Get the current user's account ID from the database
      const currentUserRef = ref(database, `users/${currentUser.uid}`);
      const currentUserSnapshot = await get(currentUserRef);
      const currentUserData = currentUserSnapshot.val();
      
      // Use primaryAccount if it exists, otherwise use userAccount
      const accountId = currentUserData?.primaryAccount || currentUserData?.userAccount;
      
      if (!accountId) {
        throw new Error('No account ID found for current user');
      }
      
      await remove_user({ 
        userId: removeDialog.user.id,
        accountId: accountId 
      });

      // Refresh the users data
      await fetchUsersData();
      setRemoveDialog({ open: false, user: null });
    } catch (error) {
      console.error('Error removing user:', error);
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleRemoveUserCancel = () => {
    setRemoveDialog({ open: false, user: null });
  };
  

  if (loading) {
    return (
      <DashboardLayout>
        <Typography variant="h2">Manage Users</Typography>
        <Typography mb={2} variant="body1">Loading user information...</Typography>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Typography variant="h2">Manage Users</Typography>
      <Typography mb={2} variant="body1">
        Invite and remove users from your FAVES account
      </Typography>

      {/* Owner Section */}
      {ownerInfo && (
        <Box mb={4}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
            Owner
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              boxShadow: (theme) => theme.shadows[1],
              border: (theme) => `1px solid ${theme.palette.divider}`,
              maxWidth: 600,
              bgcolor: 'background.paper'
            }}
          >
            <List sx={{ width: '100%', p: 0 }}>
              <ListItem 
                sx={{ p: 0 }}
              >
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  secondary={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 14, mr: 1 }} />
                        <Typography variant="body2">
                          {ownerInfo.email}
                        </Typography>
                      </Box>
                      {ownerInfo.phoneNumber && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, mr: 1 }} />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {formatPhoneNumber(ownerInfo.phoneNumber, ownerInfo.countryCode)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      )}

      {/* Users Section */}
      <Box>
        <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
          Users ({attachedUsers.length})
        </Typography>
        {getUserCapacityMessage() && (
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {getUserCapacityMessage()}
          </Typography>
        )}

        {attachedUsers.length !== 0 ? (
          <Box>
            {attachedUsers.map((user, index) => (
              <Box
                key={user.id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  boxShadow: (theme) => theme.shadows[1],
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  maxWidth: 600,
                  bgcolor: 'background.paper',
                  mb: index < attachedUsers.length - 1 ? 2 : 0
                }}
              >
                <List sx={{ width: '100%', p: 0 }}>
                  <ListItem
                    sx={{ p: 0 }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.25,
                            color: 'black',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {getUserBadgeLabel(user)}
                        </Typography>
                        {currentUser?.uid === account?.acctOwner && (
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveUser(user)}
                            title="Remove User"
                            disabled={removeLoading}
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        )}
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1">
                          {user.firstName} {user.lastName}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.1 }}>
                            <EmailIcon sx={{ fontSize: 14, mr: 1 }} />
                            <Typography variant="body2">
                              {user.email}
                            </Typography>
                          </Box>
                          {user.phoneNumber && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, mr: 1 }} />
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {formatPhoneNumber(user.phoneNumber, user.countryCode)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </List>
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {/* Intentionally left blank or could add a subtle hint if desired */}
            </Typography>
          </Box>
        )}
        {currentUser?.uid === account?.acctOwner && (
          <Box sx={{ mt: 3, maxWidth: 600, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              color="secondary"
            >
              Add User
            </Button>
          </Box>
        )}
      </Box>

      {/* Remove User Confirmation Dialog remains the same */}
      <Dialog
        open={removeDialog.open}
        onClose={handleRemoveUserCancel}
        aria-labelledby="remove-user-dialog-title"
        aria-describedby="remove-user-dialog-description"
      >
        <DialogTitle id="remove-user-dialog-title">
          Remove User
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-user-dialog-description">
            Are you sure you want to remove{' '}
            <strong>
              {removeDialog.user?.firstName} {removeDialog.user?.lastName}
            </strong>{' '}
            from your account? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveUserCancel} disabled={removeLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveUserConfirm} 
            color="error" 
            variant="contained"
            disabled={removeLoading}
          >
            {removeLoading ? 'Removing...' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};
